import * as React from "react";
import { IconButton, Button } from "./elements.js";
import { CheckIcon, CopyIcon } from "@radix-ui/react-icons";
import { Slot, Slottable } from "@radix-ui/themes";
import { useTranslation } from "./i18n/use-translation.js";

interface CopyButtonOwnProps {
  value: string;
  withIcon?: boolean;
}

interface CopyButtonProps
  extends
    CopyButtonOwnProps,
    Omit<React.ComponentProps<typeof Button>, keyof CopyButtonOwnProps> {}

export const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  function CopyButton(
    { value, asChild, children, style, withIcon = false, ...props },
    ref,
  ) {
    const Comp = asChild ? Slot : Button;
    const [hasCopied, copy] = useCopy(value);
    return (
      <Comp type="button" onPointerUp={copy} ref={ref} {...props}>
        {withIcon &&
          (hasCopied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />)}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);

interface CopyIconButtonOwnProps {
  value: string;
}

interface CopyIconButtonProps
  extends
    CopyIconButtonOwnProps,
    Omit<
      React.ComponentProps<typeof IconButton>,
      "asChild" | "children" | "title" | keyof CopyIconButtonOwnProps
    > {
  title?: string;
}

export const CopyIconButton = React.forwardRef<
  HTMLButtonElement,
  CopyIconButtonProps
>(function CopyIconButton({ value, style, ...props }, ref) {
  const [hasCopied, copy] = useCopy(value);
  const translate = useTranslation();
  const copiedText = translate({
    defaultMessage: "Copied",
    id: "xgUx3Z",
    description: "Text shown after content has been copied",
  });
  const copyToClipboardText = translate({
    defaultMessage: "Copy to clipboard",
    id: "h9d9dv",
    description: "Button text to copy content to clipboard",
  });
  return (
    <IconButton
      type="button"
      onPointerUp={copy}
      ref={ref}
      title={hasCopied ? copiedText : copyToClipboardText}
      aria-label={copyToClipboardText}
      {...props}
    >
      {hasCopied ? <CheckIcon /> : <CopyIcon />}
    </IconButton>
  );
});

function useCopy(value: string) {
  const [hasCopied, setHasCopied] = React.useState(false);
  const timeoutRef = React.useRef<number | undefined>(undefined);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setHasCopied(true);

      // Clear any existing timeout
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = window.setTimeout(() => {
        setHasCopied(false);
      }, 3000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [hasCopied, copy] as const;
}
